// i18n
import { translateInComponent, translate } from 'infras/i18n';
import translations from './i18n.json';

import { useState } from 'react';
import styled from '@emotion/styled';

import SharedEventListItem, {
  LoadingComponent,
} from 'presentations/views/components/molecules/EventListItem';
import { EventListItem } from 'presentations/api/schema/event';
import FloatSettingMenu from 'presentations/views/components/molecules/FloatSettingMenu';
import { Role } from 'domains/models/event_type_user/role';
import { Role as TeamUserRole } from 'domains/models/team_user/role';
import { viewerOfEvent } from 'presentations/views/helpers/event';
import {
  dashboardEventEditPath,
  eventEditPath,
} from 'presentations/routes/path';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import { Status } from 'domains/models/event/status';
import { ViewerItem } from 'presentations/api/schema/user';
import { PaymentMethod } from 'domains/models/event_payment/payment_method';
import { ReservationMethod } from 'domains/models/event_type/reservationMethod';
import { useRouter } from 'next/router';

interface Props {
  events: EventListItem[];
  viewer: ViewerItem;
  onCancelEvent?: any;
  isLoading?: boolean;
  isGuest: boolean;
}
export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [anchorEvent, setAnchorEvent] = useState<null | EventListItem>(null);
  const [cancelEvent, setCancelEvent] = useState<EventListItem | null>(null);
  const router = useRouter();

  const onCloseMenu = () => {
    setAnchor(null);
    setAnchorEvent(null);
  };

  const onClickMenu = (e: any, event: EventListItem) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchor(e.currentTarget);
    setAnchorEvent(event);
  };

  const onCancelEvent = async () => {
    if (!cancelEvent) return;
    await props.onCancelEvent(cancelEvent);
    setCancelEvent(null);
  };

  const cancelDialogText = () => {
    let cancelDialogText = '';
    if (cancelEvent && cancelEvent.isHost) {
      if (cancelEvent.isSeminar) {
        cancelDialogText += t(`Seminar All Cancel Notice`);
      }
      if (cancelEvent.payment?.paymentMethod === PaymentMethod.Card) {
        if (cancelDialogText.length > 0) {
          cancelDialogText += '\n\n';
        }
        cancelDialogText += t('Refund Notice');
      }
    }

    return cancelDialogText;
  };

  const onCloseDialog = () => {
    setCancelEvent(null);
  };

  const onSelectMenu = async (selectedIndex: number) => {
    if (!anchorEvent) return;

    setAnchor(null);
    if (
      anchorEvent.isParent ||
      anchorEvent.reservationMethod === ReservationMethod.Poll
    ) {
      setCancelEvent(anchorEvent);
    } else {
      if (props.isGuest) {
        switch (selectedIndex) {
          case 0:
            window.open(eventEditPath(anchorEvent.uid));
            break;
          case 1:
            setCancelEvent(anchorEvent);
            break;
          default:
            break;
        }
      } else {
        switch (selectedIndex) {
          case 0:
            router.push(
              dashboardEventEditPath(anchorEvent.uid, {
                from: encodeURIComponent(router.asPath),
              }),
            );
            break;
          case 1:
            window.open(eventEditPath(anchorEvent.uid));
            break;
          case 2:
            setCancelEvent(anchorEvent);
            break;
          default:
            break;
        }
      }
    }
  };

  if (props.isLoading) {
    return (
      <>
        <LoadingComponent />
        <LoadingComponent />
        <LoadingComponent />
      </>
    );
  } else if (props.events.length === 0) {
    return <NoContent>{t('No Content')}</NoContent>;
  }

  return (
    <>
      {props.events.map((event, index) => {
        const isSeminar = event.isSeminar;
        const isParent = event.isParent;
        const viewer = viewerOfEvent(event, props.viewer.uid);
        let isShowMenu = false;
        if (
          anchorEvent &&
          anchorEvent.id === event.id &&
          anchorEvent.isHost === event.isHost
        ) {
          isShowMenu = true;
        }
        let isEditable = false;
        if (
          event.status === Status.Open &&
          ((viewer && viewer.role <= Role.Editor) ||
            props.viewer.role <= TeamUserRole.Admin)
        ) {
          isEditable = true;
        }

        const options = [];

        if (isEditable) {
          if (
            !isSeminar ||
            (!isParent && event.reservationMethod === ReservationMethod.Event)
          ) {
            if (!props.isGuest) {
              options.push({
                icon: 'edit',
                label: t('Edit'),
              });
            }
            options.push({
              icon: 'update',
              label: t('Reschedule'),
            });
          }
          options.push({
            icon: 'close-circle',
            label: t('Cancel'),
          });
        }
        return (
          <SharedEventListItem
            onClickMenu={onClickMenu}
            key={index}
            event={event}
            viewer={props.viewer}
            menu={
              isEditable &&
              isShowMenu && (
                <FloatSettingMenu
                  onCloseMenu={onCloseMenu}
                  onSelectMenu={onSelectMenu}
                  anchor={anchor}
                  options={options}
                />
              )
            }
            isEditable={isEditable}
          />
        );
      })}
      <ConfirmationDialog
        isLoading={props.isLoading}
        isOpen={cancelEvent !== null}
        onSubmit={onCancelEvent}
        submitLabel={translate('Yes')}
        onCancel={onCloseDialog}
        cancelLabel={translate('No')}
        title={t('Cancel?')}
        text={cancelDialogText()}
      />
    </>
  );
}

const NoContent = styled.div`
  white-space: pre-wrap;
`;
